import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield } from 'lucide-react';

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('soundmoo-cookie-consent');
    if (!consent) {
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('soundmoo-cookie-consent', JSON.stringify({ necessary: true, analytics: true, marketing: true, timestamp: Date.now() }));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    localStorage.setItem('soundmoo-cookie-consent', JSON.stringify({ necessary: true, analytics: false, marketing: false, timestamp: Date.now() }));
    setIsVisible(false);
  };

  const handleSaveSettings = () => {
    localStorage.setItem('soundmoo-cookie-consent', JSON.stringify({ ...preferences, timestamp: Date.now() }));
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          className="fixed bottom-4 left-4 right-4 md:left-6 md:right-6 lg:left-auto lg:right-6 lg:max-w-lg z-[80] rounded-2xl overflow-hidden"
          data-testid="cookie-consent"
        >
          <div className="bg-[#0A1A3F]/90 backdrop-blur-xl border border-white/15 rounded-2xl p-5 md:p-6 shadow-[0_10px_50px_rgba(0,0,0,0.4)]">
            {!showSettings ? (
              <>
                <div className="flex items-start gap-3 mb-4">
                  <Shield className="w-5 h-5 text-[#19A4FB] flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="text-white font-semibold text-base font-['Outfit'] mb-2">
                      We value your experience
                    </h3>
                    <p className="text-white/70 text-sm leading-relaxed">
                      We use cookies and similar technologies to enhance your experience, analyse traffic, and improve our services.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                  <motion.button
                    onClick={handleAcceptAll}
                    className="flex-1 py-2.5 px-4 rounded-full bg-white text-[#0A1A3F] font-semibold text-sm hover:bg-white/90 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="cookie-accept-all"
                  >
                    Accept All
                  </motion.button>
                  <motion.button
                    onClick={handleRejectAll}
                    className="flex-1 py-2.5 px-4 rounded-full bg-white/10 border border-white/20 text-white font-semibold text-sm hover:bg-white/20 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="cookie-reject-all"
                  >
                    Reject All
                  </motion.button>
                  <motion.button
                    onClick={() => setShowSettings(true)}
                    className="flex-1 py-2.5 px-4 rounded-full text-white/70 font-medium text-sm hover:text-white transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    data-testid="cookie-settings-btn"
                  >
                    Cookie Settings
                  </motion.button>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-white font-semibold text-base font-['Outfit']">Cookie Settings</h3>
                  <button onClick={() => setShowSettings(false)} className="text-white/50 hover:text-white">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-3 mb-4">
                  <label className="flex items-center justify-between">
                    <span className="text-white/80 text-sm">Necessary (always on)</span>
                    <div className="w-10 h-5 bg-[#19A4FB] rounded-full relative">
                      <div className="absolute right-0.5 top-0.5 w-4 h-4 bg-white rounded-full" />
                    </div>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer" onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}>
                    <span className="text-white/80 text-sm">Analytics</span>
                    <div className={`w-10 h-5 rounded-full relative transition-colors ${preferences.analytics ? 'bg-[#19A4FB]' : 'bg-white/20'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${preferences.analytics ? 'right-0.5' : 'left-0.5'}`} />
                    </div>
                  </label>
                  <label className="flex items-center justify-between cursor-pointer" onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}>
                    <span className="text-white/80 text-sm">Marketing</span>
                    <div className={`w-10 h-5 rounded-full relative transition-colors ${preferences.marketing ? 'bg-[#19A4FB]' : 'bg-white/20'}`}>
                      <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full transition-all ${preferences.marketing ? 'right-0.5' : 'left-0.5'}`} />
                    </div>
                  </label>
                </div>
                <motion.button
                  onClick={handleSaveSettings}
                  className="w-full py-2.5 rounded-full bg-white text-[#0A1A3F] font-semibold text-sm"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  data-testid="cookie-save-settings"
                >
                  Save Preferences
                </motion.button>
              </>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
